/**
 * CONTEÚDO EDITORIAL (/conteudo)
 *
 * ORIGEM DO TEXTO — nada aqui foi escrito por IA:
 * todo o conteúdo jurídico vem da revisão oficial do escritório sobre
 * Usucapião (`textos-servicos-revisao.md`, PÁGINA 04 — a mesma base de
 * `data/services.ts` e `data/landingPages.ts`). Os artigos REORDENAM esse
 * texto por dúvida do leitor; não o reescrevem.
 *
 * O que é novo e não é afirmação jurídica: títulos, linhas de apoio, os
 * blocos de transição entre parágrafos e as chamadas.
 *
 * Qualquer alteração de conteúdo jurídico passa pelo escritório.
 *
 * NOTA SOBRE A PALAVRA "EXTRAJUDICIAL": por decisão do Dr. Luke (06/09/2026)
 * ela está fora das peças. Onde é preciso distinguir as duas vias, o texto usa
 * "a via que corre em cartório" e "a via que corre na Justiça".
 */

export type Bloco =
    | { tipo: 'p'; texto: string }
    | { tipo: 'h2'; texto: string }
    | { tipo: 'lista'; itens: string[] }
    /* Índice da página-pilar: cada item leva a um artigo. Pilar sem link
       interno não cumpre a função — é só um texto a mais. */
    | { tipo: 'indice'; itens: { texto: string; slug: string }[] }
    | { tipo: 'destaque'; texto: string }
    | { tipo: 'faq'; itens: { pergunta: string; resposta: string }[] };

export interface Artigo {
    slug: string;
    titulo: string;
    /** Linha de apoio sob o título. Também vira a meta description. */
    resumo: string;
    categoria: string;
    atualizadoEm: string;
    leituraMin: number;
    imagem: string;
    imagemAlt: string;
    corpo: Bloco[];
    /** Slugs de outros artigos, exibidos ao final. */
    relacionados: string[];
    /** Área do site para onde o CTA aponta. */
    areaCta: string;
}

const ATUALIZADO = '2026-09-06';

export const artigosData: Record<string, Artigo> = {
    'guia-usucapiao': {
        slug: 'guia-usucapiao',
        titulo: 'Usucapião: o guia de quem tem um imóvel sem escritura',
        resumo: 'O que é, quando cabe, quais documentos comprovam a posse e por qual via o pedido corre. Um índice para as dúvidas mais frequentes.',
        categoria: 'Guia',
        atualizadoEm: ATUALIZADO,
        leituraMin: 6,
        imagem: '/blog/cartorio.jpg',
        imagemAlt: 'Estantes de livros de registro em um cartório de registro de imóveis',
        corpo: [
            { tipo: 'p', texto: 'A regularização imobiliária abrange um conjunto de procedimentos jurídicos destinados a formalizar e assegurar a titularidade de imóveis.' },
            { tipo: 'p', texto: 'Na prática, quem procura o escritório costuma chegar com a mesma frase: mora ali, paga o imposto, cuidou da casa por décadas — e o papel não está no nome dele. Este guia reúne as dúvidas que aparecem a partir daí, cada uma com o seu artigo.' },
            { tipo: 'h2', texto: 'Por onde começar' },
            { tipo: 'indice', itens: [
                { texto: 'O caso mais comum é o do contrato particular, o "contrato de gaveta" — e ele tem saída.', slug: 'contrato-de-gaveta' },
                { texto: 'Não é preciso morar no imóvel para requerer a usucapião.', slug: 'preciso-morar-no-imovel' },
                { texto: 'Quem comprou de alguém que já ocupava há muito tempo pode somar as duas posses.', slug: 'soma-de-posse' },
                { texto: 'Boa parte dos documentos que comprovam a posse já está na casa do requerente.', slug: 'documentos-usucapiao' },
                { texto: 'O pedido pode correr em cartório ou na Justiça, e a escolha depende do caso.', slug: 'cartorio-ou-justica' },
                { texto: 'E o procedimento, quando corre em cartório, tem quatro etapas.', slug: 'como-funciona-usucapiao' },
            ] },
            { tipo: 'destaque', texto: 'A posse exercida por anos, e comprovada, é o que abre a porta: imposto pago, conta de consumo, correspondência recebida no endereço, imóvel mantido.' },
        ],
        relacionados: ['contrato-de-gaveta', 'preciso-morar-no-imovel', 'documentos-usucapiao'],
        areaCta: 'imobiliario',
    },

    'contrato-de-gaveta': {
        slug: 'contrato-de-gaveta',
        titulo: 'Contrato de gaveta: o que é e como regularizar o imóvel',
        resumo: 'O contrato particular prova o negócio, mas não transfere a propriedade. Como a usucapião resolve a situação de quem só tem esse papel — ou nem ele.',
        categoria: 'Usucapião',
        atualizadoEm: ATUALIZADO,
        leituraMin: 4,
        imagem: '/blog/gaveta.jpg',
        imagemAlt: 'Gaveta de madeira aberta com papéis antigos amarelados e uma caneta',
        corpo: [
            { tipo: 'p', texto: 'É mais comum do que parece: a pessoa comprou, mudou, criou os filhos ali — e o que tem na mão é um instrumento particular.' },
            { tipo: 'destaque', texto: 'O proprietário possui somente um instrumento particular, popularmente conhecido como "contrato de gaveta".' },
            { tipo: 'p', texto: 'Às vezes não há nem esse contrato. E, ainda assim, houve atuação como dono por longo período de tempo — cinco, dez ou quinze anos —, pagando impostos, pagando contas de consumo e mantendo o imóvel.' },
            { tipo: 'h2', texto: 'O que a usucapião resolve' },
            { tipo: 'p', texto: 'A usucapião é um procedimento simplificado destinado à regularização imobiliária. Em regra, ela se aplica justamente às hipóteses de imóveis irregulares como essa.' },
            { tipo: 'p', texto: 'O ponto de partida não é o papel que falta, é a posse que existe. É ela que precisa ser demonstrada.' },
            { tipo: 'h2', texto: 'O que comprova a posse' },
            { tipo: 'lista', itens: [
                'Contas de consumo — água, luz e telefonia',
                'Pagamentos de IPTU',
                'Comprovantes de melhorias realizadas no imóvel',
                'O contrato particular, se houver',
                'Imposto de renda comprovando a declaração do imóvel',
                'Correspondências recebidas no local em nome dos requerentes',
                'Contratos de aluguel, caso o imóvel seja alugado',
            ] },
            { tipo: 'p', texto: 'Não há necessidade de obter todos.' },
        ],
        relacionados: ['documentos-usucapiao', 'preciso-morar-no-imovel', 'como-funciona-usucapiao'],
        areaCta: 'imobiliario',
    },

    'preciso-morar-no-imovel': {
        slug: 'preciso-morar-no-imovel',
        titulo: 'Preciso morar no imóvel para requerer a usucapião?',
        resumo: 'Não. A usucapião pode ser requerida em imóvel desocupado, alugado e recebido por herança — inclusive durante o inventário.',
        categoria: 'Usucapião',
        atualizadoEm: ATUALIZADO,
        leituraMin: 3,
        imagem: '/blog/quarto-vazio.jpg',
        imagemAlt: 'Quarto vazio de casa antiga, com janela de veneziana entreaberta',
        corpo: [
            { tipo: 'p', texto: 'Muitas pessoas acreditam que a usucapião só se aplica a imóveis em que residam. Não é verdade.' },
            { tipo: 'h2', texto: 'As três situações' },
            { tipo: 'lista', itens: [
                'Imóveis desocupados, desde que comprovada a manutenção deste imóvel e o pagamento de tributos',
                'Imóveis alugados',
                'Imóveis decorrentes de herança — até mesmo em inventário',
            ] },
            { tipo: 'p', texto: 'O que se exige não é a moradia, é a posse: o exercício continuado de atos de dono sobre o bem, demonstrável por documentos.' },
            { tipo: 'h2', texto: 'E quando o imóvel veio de herança?' },
            { tipo: 'p', texto: 'Não é preciso fazer o inventário antes. É possível ingressar com a usucapião apenas com a comprovação da continuidade da posse que era exercida pelos pais, passando para os filhos e herdeiros. O procedimento demandará uma comprovação de soma de posse, algo simples de se atestar.' },
            { tipo: 'faq', itens: [
                {
                    pergunta: 'O imóvel foi recebido por herança. Ele pertencerá ao meu cônjuge também?',
                    resposta: 'É preciso verificar o regime de casamento. Contudo, o cônjuge poderá declarar que não possui interesse no imóvel, e a usucapião não lhe passará a propriedade. Assim como, caso queira receber o imóvel também, poderá ser feito.',
                },
            ] },
        ],
        relacionados: ['soma-de-posse', 'contrato-de-gaveta', 'documentos-usucapiao'],
        areaCta: 'imobiliario',
    },

    'como-funciona-usucapiao': {
        slug: 'como-funciona-usucapiao',
        titulo: 'Como funciona a usucapião, etapa por etapa',
        resumo: 'O caminho da via que corre em cartório: levantamento técnico, ata notarial, montagem do processo e protocolo no Registro de Imóveis.',
        categoria: 'Usucapião',
        atualizadoEm: ATUALIZADO,
        leituraMin: 4,
        imagem: '/blog/planta.jpg',
        imagemAlt: 'Mesa de trabalho com planta técnica, escalímetro e compasso',
        corpo: [
            { tipo: 'p', texto: 'A usucapião assusta pelo nome, não pela complexidade. Quando o caso permite a via que corre em cartório, o caminho é o descrito abaixo.' },
            { tipo: 'h2', texto: '1. Levantamento técnico' },
            { tipo: 'p', texto: 'Primeiramente, em muitos casos, é realizado um levantamento por um engenheiro parceiro, que elaborará uma planta topográfica, um memorial descritivo e a ART.' },
            { tipo: 'h2', texto: '2. Ata notarial' },
            { tipo: 'p', texto: 'Após, o material será enviado ao Tabelião de Notas para a elaboração da ata notarial, documento que servirá para atestar a posse sobre o imóvel.' },
            { tipo: 'h2', texto: '3. Montagem do processo' },
            { tipo: 'p', texto: 'Na sequência, o escritório montará o processo e emitirá todas as certidões pertinentes.' },
            { tipo: 'h2', texto: '4. Protocolo' },
            { tipo: 'p', texto: 'Por fim, o pedido é protocolado perante o Registro de Imóveis competente, onde se processará.' },
            { tipo: 'destaque', texto: 'Nenhuma dessas etapas é audiência. Cada caso, porém, tem um documento a mais ou a menos.' },
        ],
        relacionados: ['cartorio-ou-justica', 'documentos-usucapiao', 'contrato-de-gaveta'],
        areaCta: 'imobiliario',
    },

    'soma-de-posse': {
        slug: 'soma-de-posse',
        titulo: 'Comprei um imóvel irregular há pouco tempo. Ainda posso requerer a usucapião?',
        resumo: 'Sim. A soma de posse permite acrescentar à sua o tempo de posse do proprietário anterior.',
        categoria: 'Usucapião',
        atualizadoEm: ATUALIZADO,
        leituraMin: 3,
        imagem: '/blog/camadas.jpg',
        imagemAlt: 'Muro antigo com várias camadas de tinta descascada, expondo cores de décadas diferentes',
        corpo: [
            { tipo: 'p', texto: 'É a dúvida de quem comprou de alguém que morava ali havia muito tempo e teme ter chegado tarde.' },
            { tipo: 'destaque', texto: 'Esta é a vantagem da usucapião: você poderá requerer a soma de posse com o proprietário anterior.' },
            { tipo: 'p', texto: 'Se o antigo proprietário já detinha posse por um longo período, você poderá requerer que essa posse seja acrescida à sua, resultando no cumprimento dos requisitos para o deferimento da usucapião.' },
            { tipo: 'h2', texto: 'Vale também para herança' },
            { tipo: 'p', texto: 'A mesma lógica se aplica quando o imóvel passa de pais para filhos: comprova-se a continuidade da posse, sem necessidade de inventário prévio.' },
        ],
        relacionados: ['preciso-morar-no-imovel', 'documentos-usucapiao', 'guia-usucapiao'],
        areaCta: 'imobiliario',
    },

    'documentos-usucapiao': {
        slug: 'documentos-usucapiao',
        titulo: 'Documentos para usucapião: a lista completa',
        resumo: 'O que reunir para requerer a usucapião — e por que boa parte disso já está na sua casa.',
        categoria: 'Usucapião',
        atualizadoEm: ATUALIZADO,
        leituraMin: 3,
        imagem: '/blog/caixa-papeis.jpg',
        imagemAlt: 'Caixa de papelão sobre mesa de madeira, cheia de pastas e papéis antigos',
        corpo: [
            { tipo: 'p', texto: 'Para requerer a usucapião não basta o documento pessoal: é preciso comprovar a posse. E o que comprova posse é o cotidiano.' },
            { tipo: 'h2', texto: 'Documentos pessoais e do imóvel' },
            { tipo: 'lista', itens: [
                'Documentos pessoais dos requerentes — RG, CPF ou CNH',
                'Certidão de nascimento e/ou casamento',
                'Comprovante de residência atualizado',
                'Documentos do imóvel — matrícula, IPTU e contratos',
            ] },
            { tipo: 'h2', texto: 'Documentos que comprovam a posse' },
            { tipo: 'p', texto: 'Não há necessidade de obter todos.' },
            { tipo: 'lista', itens: [
                'Contas de consumo — água, luz, telefonia',
                'Pagamentos de impostos — IPTU',
                'Comprovantes de melhorias realizadas no imóvel — reformas',
                'Contrato particular, se houver — o "contrato de gaveta"',
                'Imposto de renda comprovando a declaração do imóvel',
                'Correspondências recebidas no local do imóvel em nome dos requerentes',
                'Contratos de aluguel, caso o imóvel seja alugado',
            ] },
            { tipo: 'destaque', texto: 'Junte o que tiver em casa. A análise dirá o que ainda falta e o que pode ser dispensado.' },
        ],
        relacionados: ['contrato-de-gaveta', 'como-funciona-usucapiao', 'preciso-morar-no-imovel'],
        areaCta: 'imobiliario',
    },

    'cartorio-ou-justica': {
        slug: 'cartorio-ou-justica',
        titulo: 'Cartório ou Justiça: qual via cabe ao seu caso',
        resumo: 'A usucapião tem duas vias, e cada uma tem uma desvantagem que precisa ser dita antes da escolha.',
        categoria: 'Usucapião',
        atualizadoEm: ATUALIZADO,
        leituraMin: 3,
        imagem: '/blog/duas-portas.jpg',
        imagemAlt: 'Duas portas de madeira antigas, lado a lado, no corredor de um prédio público',
        corpo: [
            { tipo: 'p', texto: 'Nem sempre o caminho mais rápido é o melhor caminho. A escolha entre as duas vias se define depois de olhar o caso, não antes.' },
            { tipo: 'h2', texto: 'A via que corre em cartório' },
            { tipo: 'p', texto: 'A vantagem é que se trata de um procedimento mais rápido. Em contrapartida, por se tratar de procedimento que corre diretamente em cartórios, todos os pagamentos são à vista e de valores mais elevados.' },
            { tipo: 'h2', texto: 'A via que corre na Justiça' },
            { tipo: 'p', texto: 'Muitas vezes, em hipóteses de baixa renda, é recomendada a via judicial para requerimento de isenção de diversas taxas, através da Justiça Gratuita, garantindo o acesso até às famílias com menor renda à regularização do seu imóvel.' },
            { tipo: 'p', texto: 'A desvantagem é que este procedimento levará mais tempo.' },
            { tipo: 'destaque', texto: 'Regularizar o imóvel deixa de depender de ter o valor das taxas à vista — é por isso que a via mais lenta existe.' },
        ],
        relacionados: ['como-funciona-usucapiao', 'guia-usucapiao', 'documentos-usucapiao'],
        areaCta: 'imobiliario',
    },
};

/** Ordem de exibição no índice — o guia primeiro, depois a jornada da dúvida. */
export const artigosOrdem = [
    'guia-usucapiao',
    'contrato-de-gaveta',
    'preciso-morar-no-imovel',
    'como-funciona-usucapiao',
    'soma-de-posse',
    'documentos-usucapiao',
    'cartorio-ou-justica',
];

export const listarArtigos = (): Artigo[] =>
    artigosOrdem.map((slug) => artigosData[slug]).filter(Boolean);
